import { useState } from 'react';
import { PlusOutlined, StopOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Link } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type { Subscriber } from '@titularis/api-client';
import { api } from '../../api';
import { formatarData } from '../../dominio';
import { useOpcoesDeChave } from '../../opcoesDeChave';

/**
 * Acessos: uma linha por par pessoa-produto, e o conserto quando algo dá errado.
 *
 * **Continua existindo ao lado da lista de pessoas porque responde outra
 * pergunta.** A lista diz "esta pessoa tem acesso"; esta aba diz "quem tem
 * acesso a *este* produto" - e uma pessoa com três leis assinadas é três linhas
 * aqui e uma só lá. Fundir as duas obrigaria a escolher qual das duas perguntas
 * deixa de ter resposta.
 *
 * Era a tela de Assinantes, item próprio do menu até 18/09/2026. O que mudou foi
 * o lugar: o nome do assinante passa a levar à ficha dele, que é onde o resto da
 * história está.
 *
 * **Duas origens, e a distinção importa.** `purchase` veio do provedor de
 * pagamento; `manual` foi cortesia, teste ou conserto. As duas liberam o mesmo
 * conteúdo e não são a mesma coisa: uma tem dinheiro atrás, a outra tem uma
 * decisão de alguém - e é por isso que a concessão à mão pede um motivo, que vai
 * para a trilha de auditoria.
 */

const PAGE_SIZE = 20;

interface UsuarioEncontrado {
  id: string;
  nome: string;
  email: string;
}

export function AcessosDasPessoas({ ehDono }: { ehDono: boolean }) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { grupos, carregando: carregandoChaves } = useOpcoesDeChave();

  const { ler, filtrar, irParaPagina } = useEstadoDeLista();

  /*
   * **O produto vai como veio.** Ele era conferido contra a lista de chaves
   * fixas, e desde a produtização o conjunto é aberto: conferir aqui exigiria
   * repetir no bundle o vocabulário que o servidor já valida (D-45), e esperar
   * as leis carregarem para aceitar um filtro que veio pronto na URL.
   */
  const produto = ler.texto('produto');
  const situacao = (['ativos', 'vencidos'] as const).find(
    (valor) => valor === ler.texto('vigencia'),
  );
  const page = ler.pagina();

  const [concedendo, setConcedendo] = useState(false);
  const [buscaDeUsuario, setBuscaDeUsuario] = useState('');
  const [form] = Form.useForm<{
    userId: string;
    product: string;
    meses: number;
    motivo?: string;
  }>();

  const assinantes = useQuery({
    queryKey: ['assinantes', { produto, situacao, page }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.listSubscribers({
        ...(produto ? { product: produto } : {}),
        ...(situacao ? { situacao } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  /*
   * O seletor de usuário busca no servidor, pela mesma rota que a lista de
   * pessoas usa: a plataforma terá contas demais para carregar todas.
   */
  const usuarios = useQuery({
    queryKey: ['pessoas-para-conceder', buscaDeUsuario],
    enabled: concedendo && buscaDeUsuario.length >= 3,
    queryFn: async () => {
      const pagina = await api.listPeople({ busca: buscaDeUsuario, pageSize: 10 });
      return pagina.items.map<UsuarioEncontrado>((pessoa) => ({
        id: pessoa.id,
        nome: pessoa.nome,
        email: pessoa.email,
      }));
    },
  });

  const conceder = useMutation({
    mutationFn: (dados: { userId: string; product: string; meses: number; motivo?: string }) =>
      api.grantEntitlement(dados),
    onSuccess: async () => {
      message.success('Acesso concedido.');
      setConcedendo(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['assinantes'] });
      // O funil e a lista contam assinante: sem isto eles ficam um passo atrás.
      await queryClient.invalidateQueries({ queryKey: ['pessoas'] });
      await queryClient.invalidateQueries({ queryKey: ['resumo-das-pessoas'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const revogar = useMutation({
    mutationFn: (alvo: Subscriber) => api.revokeEntitlement(alvo.userId, alvo.product),
    onSuccess: async () => {
      message.success('Acesso revogado.');
      await queryClient.invalidateQueries({ queryKey: ['assinantes'] });
      await queryClient.invalidateQueries({ queryKey: ['pessoas'] });
      await queryClient.invalidateQueries({ queryKey: ['resumo-das-pessoas'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  function confirmarRevogacao(alvo: Subscriber) {
    modal.confirm({
      title: 'Revogar o acesso?',
      /*
       * O aviso muda com a origem, porque a consequência muda: revogar uma
       * compra tira acesso de quem pagou, e isso costuma vir acompanhado de
       * estorno. A tela não impede - quem decide é quem opera -, mas não deixa
       * a diferença passar despercebida.
       */
      content:
        alvo.source === 'purchase'
          ? `${alvo.nome ?? alvo.userId} pagou por este acesso. Revogar não estorna nada: o estorno se faz no painel do provedor, e é o estorno que retira o acesso sozinho.`
          : alvo.source === 'purchase-sandbox'
            ? `${alvo.nome ?? alvo.userId} recebeu este acesso por uma compra no sandbox: nenhum valor entrou, então não há o que estornar. Revogar retira o acesso imediatamente.`
            : `${alvo.nome ?? alvo.userId} perde o acesso a ${alvo.rotulo} imediatamente.`,
      okText: 'Revogar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => revogar.mutateAsync(alvo),
    });
  }

  const colunas = [
    {
      title: 'Assinante',
      key: 'assinante',
      render: (_: unknown, linha: Subscriber) =>
        linha.nome ? (
          <Space orientation="vertical" size={0}>
            {/* O nome leva à ficha: é lá que está o resto da história. */}
            <Link to={`/pessoas/${encodeURIComponent(linha.userId)}`}>{linha.nome}</Link>
            <Typography.Text type="secondary">{linha.email}</Typography.Text>
          </Space>
        ) : (
          // Conta excluída: o direito fica, e dizer isso é melhor que mostrar um id nu.
          <Tooltip title={`Conta excluída. Id: ${linha.userId}`}>
            <Typography.Text type="secondary">conta excluída</Typography.Text>
          </Tooltip>
        ),
    },
    {
      title: 'Produto',
      dataIndex: 'rotulo',
      key: 'product',
    },
    {
      title: 'Origem',
      dataIndex: 'source',
      key: 'source',
      /*
       * Três origens, e a do meio é a que faltava. `purchase-sandbox` é compra
       * de verdade em tudo - nasce de pedido e o estorno a revoga -, mas com
       * dinheiro de mentira. Mostrá-la como "compra" faria a tela afirmar
       * receita que não existiu; mostrá-la como "concedido" esconderia que há um
       * pedido atrás dela.
       */
      render: (valor: string) => {
        if (valor === 'purchase') return <Tag color="green">compra</Tag>;
        if (valor === 'purchase-sandbox') {
          return (
            <Tooltip title="Compra fechada no sandbox do provedor: nenhum valor entrou. O acesso é real e o estorno do pedido o retira.">
              <Tag color="orange">compra de ensaio</Tag>
            </Tooltip>
          );
        }
        return <Tag>concedido</Tag>;
      },
    },
    {
      title: 'Até',
      key: 'expiresAt',
      render: (_: unknown, linha: Subscriber) =>
        linha.expiresAt ? (
          <Space size={6}>
            {formatarData(linha.expiresAt)}
            {!linha.ativo && <Tag color="red">vencido</Tag>}
          </Space>
        ) : (
          <Tag color="blue">sem prazo</Tag>
        ),
    },
    {
      title: 'Desde',
      key: 'createdAt',
      render: (_: unknown, l: Subscriber) => formatarData(l.createdAt),
    },
    ...(ehDono
      ? [
          {
            title: '',
            key: 'acoes',
            width: 56,
            render: (_: unknown, linha: Subscriber) => (
              <Tooltip title="Revogar">
                <Button
                  type="text"
                  danger
                  icon={<StopOutlined />}
                  loading={revogar.isPending && revogar.variables?.userId === linha.userId}
                  onClick={() => confirmarRevogacao(linha)}
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Flex justify="space-between" align="center" wrap gap={12}>
        <Flex gap={12} wrap>
          <Segmented
            value={situacao ?? 'todos'}
            onChange={(valor) =>
              filtrar({ vigencia: valor === 'todos' ? undefined : String(valor) })
            }
            options={[
              { label: 'Todos', value: 'todos' },
              { label: 'Ativos', value: 'ativos' },
              { label: 'Vencidos', value: 'vencidos' },
            ]}
          />
          <Select
            allowClear
            placeholder="Todos os produtos"
            /*
             * 260, e não 200: com as Legislações Comentadas na lista, "Código de
             * Processo Civil" era cortado em "Código de Processo ..." - visto no
             * navegador em 07/09/2026. O CNN corta de qualquer jeito, e para esse
             * não há largura que resolva.
             */
            style={{ minWidth: 260 }}
            value={produto ?? undefined}
            onChange={(valor) => filtrar({ produto: valor ?? undefined })}
            options={grupos}
            optionFilterProp="label"
            showSearch
          />
        </Flex>

        {ehDono && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setConcedendo(true)}>
            Conceder acesso
          </Button>
        )}
      </Flex>

      {assinantes.error && (
        <Alert type="error" showIcon title={(assinantes.error as Error).message} />
      )}

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          rowKey={(linha) => `${linha.userId}:${linha.product}`}
          dataSource={assinantes.data?.items ?? []}
          columns={colunas}
          loading={assinantes.isLoading}
          locale={{
            emptyText: (
              <Empty
                description={
                  situacao || produto
                    ? 'Nenhum acesso com esses filtros.'
                    : 'Ninguém tem acesso pago ainda.'
                }
              />
            ),
          }}
          pagination={{
            current: assinantes.data?.page ?? page,
            pageSize: PAGE_SIZE,
            total: assinantes.data?.total ?? 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (pagina: number) => irParaPagina(pagina),
          }}
          scroll={{ x: 820 }}
        />
      </Card>

      <Modal
        open={concedendo}
        title="Conceder acesso"
        okText="Conceder"
        confirmLoading={conceder.isPending}
        onCancel={() => setConcedendo(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ meses: 12, product: 'enac' }}
          onFinish={(valores) => conceder.mutate(valores)}
        >
          <Form.Item
            name="userId"
            label="Quem"
            rules={[{ required: true, message: 'Escolha o usuário.' }]}
            extra="Busque pelo nome ou pelo e-mail, com pelo menos três letras."
          >
            <Select
              showSearch
              placeholder="nome ou e-mail"
              filterOption={false}
              onSearch={setBuscaDeUsuario}
              loading={usuarios.isFetching}
              notFoundContent={
                buscaDeUsuario.length < 3 ? 'Digite ao menos três letras' : 'Ninguém encontrado'
              }
              options={(usuarios.data ?? []).map((usuario) => ({
                value: usuario.id,
                label: `${usuario.nome} - ${usuario.email}`,
              }))}
            />
          </Form.Item>

          <Form.Item name="product" label="Produto" rules={[{ required: true }]}>
            {/*
              Agrupado e buscável: com uma Legislação Comentada por linha, a
              lista de chaves deixou de caber num seletor simples (D-45).
            */}
            <Select
              options={grupos}
              optionFilterProp="label"
              showSearch
              loading={carregandoChaves}
            />
          </Form.Item>

          <Form.Item
            name="meses"
            label="Meses de acesso"
            rules={[{ required: true }]}
            extra="Zero concede sem prazo."
          >
            <InputNumber min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="motivo"
            label="Motivo"
            extra="Fica na trilha. É o que responde, seis meses depois, por que essa pessoa tem acesso."
          >
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
